import apiClient from './axios';
import { ApiResponse } from '../types';
import { MaybeApproval } from './approvals';

/**
 * Aggregators: people who bring businesses to PayIQ Business and earn a
 * share of what PayIQ makes from each one.
 *
 * Reviewing applications and managing an aggregator is `aggregators.manage`.
 * The rules are `aggregators.settings`. Settling a stuck withdrawal and
 * crediting a business to an aggregator by hand go to a second admin.
 */

export type RevenueSource = 'transfer_markup' | 'vas_commission' | 'subscription' | 'message_fees';

export interface AggregatorSettings {
  applicationsOpen: boolean;
  defaultCommissionPercent: number;
  /** Null: for as long as the business uses PayIQ. */
  earningWindowMonths: number | null;
  selfDealing: 'flag' | 'block';
  withholdingTaxPercent: number;
  minimumWithdrawal: number;
  revenueSources: RevenueSource[];
  availableSources: Array<{ key: RevenueSource; label: string }>;
}

export type AggregatorSettingsPatch = Partial<Omit<AggregatorSettings, 'availableSources'>>;

export interface Person {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  tier: string | null;
}

export interface AggregatorApplication {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  about: string;
  location: string;
  businessesPerMonth: number;
  rejectionReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  user: Person;
}

export type AggregatorStatus = 'active' | 'suspended' | 'revoked';

export interface AggregatorRow {
  id: string;
  userId: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: AggregatorStatus;
  commissionPercent: number;
  withdrawalsPaused: boolean;
  approvedAt: string;
  businessCount: number;
  earned: number;
  flaggedBusinesses: number;
}

export interface AggregatorBalance {
  earned: number;
  available: number;
  pending: number;
  paidOut: number;
  thisMonth: number;
}

export interface AggregatorBusiness {
  id: string;
  name: string;
  joinedAt: string;
  volumeThisMonth: number;
  volumeLastMonth: number;
  earned: number;
  earning: boolean;
  earningEndsAt: string | null;
}

export interface AggregatorReview {
  id: string;
  businessId: string;
  businessName: string;
  status: 'flagged' | 'blocked' | 'cleared';
  reason: string;
  reviewedAt: string | null;
  createdAt: string;
}

export interface AggregatorPayout {
  id: string;
  reference: string;
  gross: number;
  withholdingTax: number;
  net: number;
  status: 'pending' | 'paid' | 'failed';
  createdAt: string;
  settledAt: string | null;
  aggregatorId?: string;
}

export interface AggregatorEvent {
  id: string;
  kind: string;
  detail: Record<string, unknown> | null;
  actorId: string | null;
  createdAt: string;
}

export interface AggregatorDetail {
  aggregator: {
    id: string;
    userId: string;
    status: AggregatorStatus;
    statusReason: string | null;
    commissionPercent: number;
    withdrawalsPaused: boolean;
    withdrawalsPausedReason: string | null;
    approvedAt: string;
    earningSince: string;
  };
  person: Person | null;
  application: { about: string; location: string; businessesPerMonth: number; createdAt: string } | null;
  balance: AggregatorBalance;
  businesses: Page<AggregatorBusiness>;
  history: AggregatorEvent[];
  reviews: AggregatorReview[];
  payouts: AggregatorPayout[];
}

export interface AggregatorEarning {
  id: string;
  businessId: string;
  businessName: string;
  source: string;
  revenueAmount: number;
  commissionPercent: number;
  amount: number;
  correction: boolean;
  occurredAt: string;
}

export interface Page<T> {
  items: T[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

const unwrap = <T,>(p: Promise<{ data: ApiResponse<T> }>) => p.then((r) => r.data.data);

export const aggregatorsApi = {
  settings: () => unwrap(apiClient.get<ApiResponse<AggregatorSettings>>('/admin/aggregators/settings')),
  updateSettings: (patch: AggregatorSettingsPatch) =>
    unwrap(apiClient.patch<ApiResponse<AggregatorSettings>>('/admin/aggregators/settings', patch)),

  applications: (params: { status?: string; page?: number; limit?: number }) =>
    unwrap(apiClient.get<ApiResponse<Page<AggregatorApplication>>>('/admin/aggregators/applications', { params })),
  /** Leave the rate out to use the default in settings. */
  approve: (id: string, commissionPercent?: number) =>
    unwrap(
      apiClient.post<ApiResponse<unknown>>(
        `/admin/aggregators/applications/${id}/approve`,
        commissionPercent === undefined ? {} : { commissionPercent },
      ),
    ),
  reject: (id: string, reason: string) =>
    unwrap(apiClient.post<ApiResponse<unknown>>(`/admin/aggregators/applications/${id}/reject`, { reason })),

  list: (params: { q?: string; status?: string; page?: number; limit?: number }) =>
    unwrap(apiClient.get<ApiResponse<Page<AggregatorRow>>>('/admin/aggregators', { params })),
  detail: (id: string) => unwrap(apiClient.get<ApiResponse<AggregatorDetail>>(`/admin/aggregators/${id}`)),
  earnings: (id: string, page = 1) =>
    unwrap(
      apiClient.get<ApiResponse<Page<AggregatorEarning>>>(`/admin/aggregators/${id}/earnings`, {
        params: { page, limit: 25 },
      }),
    ),

  setRate: (id: string, commissionPercent: number, note?: string) =>
    unwrap(
      apiClient.patch<ApiResponse<unknown>>(`/admin/aggregators/${id}/rate`, {
        commissionPercent,
        ...(note ? { note } : {}),
      }),
    ),
  pauseWithdrawals: (id: string, reason: string) =>
    unwrap(apiClient.post<ApiResponse<unknown>>(`/admin/aggregators/${id}/withdrawals/pause`, { reason })),
  resumeWithdrawals: (id: string) =>
    unwrap(apiClient.post<ApiResponse<unknown>>(`/admin/aggregators/${id}/withdrawals/resume`)),
  suspend: (id: string, reason: string) =>
    unwrap(apiClient.post<ApiResponse<unknown>>(`/admin/aggregators/${id}/suspend`, { reason })),
  reinstate: (id: string) => unwrap(apiClient.post<ApiResponse<unknown>>(`/admin/aggregators/${id}/reinstate`, {})),
  revoke: (id: string, reason: string) =>
    unwrap(apiClient.post<ApiResponse<unknown>>(`/admin/aggregators/${id}/revoke`, { reason })),

  reviewBusiness: (reviewId: string, status: 'cleared' | 'blocked') =>
    unwrap(apiClient.post<ApiResponse<unknown>>(`/admin/aggregators/reviews/${reviewId}`, { status })),

  stuckPayouts: () => unwrap(apiClient.get<ApiResponse<AggregatorPayout[]>>('/admin/aggregators/payouts/stuck')),
  /** Maker-checker: may come back as an approval waiting on a second admin. */
  resolvePayout: (id: string, outcome: 'completed' | 'failed') =>
    unwrap(
      apiClient.post<ApiResponse<MaybeApproval<unknown>>>(`/admin/aggregators/payouts/${id}/resolve`, { outcome }),
    ),
  /** Maker-checker. Null removes the business from its aggregator. */
  assignBusiness: (businessId: string, aggregatorId: string | null) =>
    unwrap(
      apiClient.post<ApiResponse<MaybeApproval<unknown>>>(`/admin/aggregators/businesses/${businessId}/assign`, {
        aggregatorId,
      }),
    ),
};
